/**
 * App admins: users who can edit/delete any event and any group.
 * Stored in Firestore: admins/{userId}. Only existing admins can read/write.
 * First admin: add document admins/{yourUserId} in Firebase Console (e.g. { addedAt: now }).
 */
import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';

const ADMINS_COLLECTION = 'admins';

/**
 * Get all admin user IDs.
 */
export const getAdminIds = async () => {
  const snapshot = await getDocs(collection(db, ADMINS_COLLECTION));
  return snapshot.docs.map((d) => d.id);
};

/**
 * Check if a user is an app admin.
 */
export const isAdmin = async (userId) => {
  if (!userId) return false;
  const ref = doc(db, ADMINS_COLLECTION, userId);
  const snap = await getDoc(ref);
  return snap.exists();
};

/**
 * Add an admin by user ID. Caller must already be an admin.
 */
export const addAdmin = async (adminUserId, addedByUserId) => {
  const ref = doc(db, ADMINS_COLLECTION, adminUserId);
  await setDoc(ref, {
    addedAt: new Date(),
    addedBy: addedByUserId || null,
  });
};

/**
 * Remove an admin. Caller must be an admin.
 */
export const removeAdmin = async (userId) => {
  const ref = doc(db, ADMINS_COLLECTION, userId);
  await deleteDoc(ref);
};

/**
 * Look up a user by phone number (E.164 or digits). Returns { userId, firstName, lastName } or null.
 */
export const lookupUserByPhone = async (phone) => {
  if (!phone || !phone.replace(/\D/g, '')) return null;
  const normalized = phone.trim().startsWith('+') ? phone.trim() : `+${phone.trim().replace(/\D/g, '')}`;
  const digits = normalized.replace(/\D/g, '');

  const { collection: col, query: q, where, getDocs: getDocsFn } = await import('firebase/firestore');
  const usersRef = col(db, 'users');

  try {
    const phoneQuery = q(usersRef, where('phone', '==', normalized));
    const snap = await getDocsFn(phoneQuery);
    if (!snap.empty) {
      const d = snap.docs[0];
      const data = d.data();
      return {
        userId: d.id,
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
      };
    }
  } catch (e) {
    // Index may not exist; try phoneNumber (digits)
  }

  try {
    const phoneNumberQuery = query(usersRef, where('phoneNumber', '==', digits));
    const snap = await getDocs(phoneNumberQuery);
    if (!snap.empty) {
      const d = snap.docs[0];
      const data = d.data();
      return {
        userId: d.id,
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
      };
    }
  } catch (e2) {
    console.warn('lookupUserByPhone:', e2);
  }

  return null;
};

/**
 * Add an admin by phone number. Looks up user by phone, then adds them as admin.
 * Caller must already be an admin. Returns { success: true, userId } or { success: false, error }.
 */
export const addAdminByPhone = async (phone, addedByUserId) => {
  const user = await lookupUserByPhone(phone);
  if (!user) return { success: false, error: 'No user found with that phone number' };
  await addAdmin(user.userId, addedByUserId);
  return { success: true, userId: user.userId };
};
